import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Space, Typography } from "antd";
import { formatUsd } from "../utils";

const { Text } = Typography;

const _severityToType = (level) => {
  const v = String(level || "").toLowerCase();
  if (v === "critical") return "error";
  if (v === "warning") return "warning";
  return "info";
};

const _warningKey = (w) => {
  const scope = String(w.scope || "").toLowerCase();
  const type = String(w.type || "unknown").toLowerCase();
  const threshold = w.threshold_pct != null ? String(w.threshold_pct) : "na";
  return `${scope}:${type}:${threshold}`;
};

const _storageKey = (projectId) => `budget-warnings-dismissed:${projectId || "global"}`;

const _loadDismissed = (projectId) => {
  try {
    const raw = sessionStorage.getItem(_storageKey(projectId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
};

const _saveDismissed = (projectId, keys) => {
  try {
    sessionStorage.setItem(_storageKey(projectId), JSON.stringify([...keys]));
  } catch {
    /* ignore */
  }
};

export default function BudgetWarningsBanner({
  warnings = [],
  onRequestExtension,
  projectId = null,
  showProjectWarnings = false,
}) {
  const [dismissed, setDismissed] = useState(() => _loadDismissed(projectId));

  useEffect(() => {
    setDismissed(_loadDismissed(projectId));
  }, [projectId]);

  const rows = useMemo(() => {
    const list = Array.isArray(warnings) ? warnings.filter(Boolean) : [];
    if (showProjectWarnings) return list;
    return list.filter((w) => String(w.scope || "").toLowerCase() !== "project");
  }, [warnings, showProjectWarnings]);

  const visibleRows = useMemo(
    () => rows.filter((w) => !dismissed.has(_warningKey(w))),
    [rows, dismissed],
  );

  const dismissOne = useCallback(
    (key) => {
      setDismissed((prev) => {
        const next = new Set(prev);
        next.add(key);
        _saveDismissed(projectId, next);
        return next;
      });
    },
    [projectId],
  );

  const dismissAll = useCallback(() => {
    const keys = rows.map((w) => _warningKey(w));
    setDismissed((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => next.add(k));
      _saveDismissed(projectId, next);
      return next;
    });
  }, [rows, projectId]);

  if (!visibleRows.length) return null;

  return (
    <Space direction="vertical" size={8} style={{ width: "100%" }}>
      {visibleRows.length > 1 ? (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button type="link" size="small" onClick={dismissAll} style={{ padding: 0, height: "auto" }}>
            Dismiss all
          </Button>
        </div>
      ) : null}
      {visibleRows.map((w) => {
        const key = _warningKey(w);
        const scope = String(w.scope || "").toLowerCase();
        const utilPct = Number(w.utilization_pct);
        const thresholdPct = Number(w.threshold_pct);
        const atOrAbove80 =
          (Number.isFinite(utilPct) && utilPct >= 80)
          || (Number.isFinite(thresholdPct) && thresholdPct >= 80);
        const canRequest =
          scope === "user"
          && atOrAbove80
          && typeof onRequestExtension === "function";
        const title =
          scope === "project"
            ? "Project budget notice"
            : scope === "user"
              ? "Your budget notice"
              : "Budget notice";
        return (
          <Alert
            key={key}
            showIcon
            closable
            type={_severityToType(w.level)}
            message={title}
            onClose={() => dismissOne(key)}
            description={
              <div>
                <div style={{ marginBottom: 4 }}>{w.message}</div>
                <Text type="secondary">
                  Spent {formatUsd(w.spent_usd)} / {formatUsd(w.limit_usd)}
                  {Number(w.remaining_usd) > 0 ? ` (${formatUsd(w.remaining_usd)} remaining)` : ""}
                </Text>
              </div>
            }
            action={
              canRequest ? (
                <Button size="small" onClick={() => onRequestExtension()}>
                  Request extension
                </Button>
              ) : null
            }
          />
        );
      })}
    </Space>
  );
}
